import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { WEEKDAYS_FULL, formatDateFR } from "@/lib/dates";
import {
  Snowflake, LogOut, Calendar, Wrench, Clock, Loader2, Plus, Trash2, Pencil, X, Check,
  Phone, Car, Users, TrendingUp, CalendarDays, Save, ChevronLeft, ChevronRight, Ban, Mail, Send, Power, Eye, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import PlanningCalendar from "@/components/PlanningCalendar";

const inputCls = "w-full bg-[#050810] border border-[#1A2642] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] mb-1.5";

const statusMap = {
  confirmed: { label: "Confirmée", cls: "bg-[#06D6A0]/15 text-[#06D6A0] border-[#06D6A0]/30" },
  cancelled: { label: "Annulée", cls: "bg-[#EF476F]/15 text-[#EF476F] border-[#EF476F]/30" },
  done: { label: "Terminée", cls: "bg-[#00B4D8]/15 text-[#00B4D8] border-[#00B4D8]/30" },
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => { localStorage.removeItem("climomax_admin_token"); nav("/admin/login"); };

  if (!authed) return <div className="min-h-screen flex items-center justify-center bg-[#050810]"><Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" /></div>;

  return (
    <div className="min-h-screen bg-[#050810] text-white">
      <nav className="border-b border-[#1A2642] bg-[#0C1322]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo className="h-8 w-auto" /><span className="text-xs text-[#475569] hidden sm:inline">· Espace gérant</span></div>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-white transition"><LogOut className="w-4 h-4" /> Déconnexion</button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex gap-2 mb-8 border-b border-[#1A2642] overflow-x-auto no-scrollbar -mx-5 px-5">
          {[["bookings", "Réservations", Calendar], ["planning", "Planning", CalendarDays], ["services", "Prestations", Wrench], ["hours", "Horaires", Clock], ["emails", "Emails", Mail]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition shrink-0 whitespace-nowrap ${tab === key ? "border-[#00B4D8] text-white" : "border-transparent text-[#94A3B8] hover:text-white"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsTab />}
        {tab === "planning" && <PlanningCalendar />}
        {tab === "services" && <ServicesTab />}
        {tab === "hours" && <HoursTab />}
        {tab === "emails" && <EmailsTab />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-[#00B4D8]"}`} />
      </div>
      <p className="font-heading text-3xl font-black mt-1">{value}</p>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("upcoming");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/stats")]);
      setBookings(b.data); setStats(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/bookings/${id}/status`, null, { params: { status } }); toast.success("Mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const filtered = bookings.filter((b) => {
    if (filter === "upcoming") return b.status === "confirmed" && b.date >= today;
    if (filter === "today") return b.date === today;
    if (filter === "cancelled") return b.status === "cancelled";
    return true;
  });

  if (loading) return <Loader2 className="w-7 h-7 animate-spin text-[#00B4D8]" />;

  return (
    <div data-testid="bookings-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={CalendarDays} label="Aujourd'hui" value={stats?.today ?? 0} />
        <Stat icon={Calendar} label="À venir" value={stats?.upcoming ?? 0} />
        <Stat icon={Users} label="Clients" value={stats?.customers ?? 0} accent="text-[#FFD60A]" />
        <Stat icon={TrendingUp} label="CA à venir" value={`${stats?.upcoming_revenue ?? 0} €`} accent="text-[#FFD60A]" />
      </div>

      <div className="flex gap-2 mb-5">
        {[["upcoming", "À venir"], ["today", "Aujourd'hui"], ["cancelled", "Annulées"], ["all", "Toutes"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition ${filter === k ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "border-[#1A2642] text-[#94A3B8] hover:border-[#00B4D8]"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? <p className="text-[#94A3B8] py-8">Aucune réservation.</p> : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <div key={b.id} className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-booking-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${statusMap[b.status].cls}`}>{statusMap[b.status].label}</span>
                  <span className="font-bold">{b.service_name}</span>
                  <span className="text-[#FFD60A] font-bold">{b.service_price} €</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-[#94A3B8]">
                  <span className="flex items-center gap-1.5 text-white"><Calendar className="w-4 h-4 text-[#00B4D8]" />{formatDateFR(b.date)} · {b.start_time}-{b.end_time}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{b.customer_name}</span>
                  <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" />{b.customer_phone}</span>
                  <span className="flex items-center gap-1.5"><Car className="w-4 h-4" />{b.vehicle_marque} {b.vehicle_modele} ({b.vehicle_annee}){b.vehicle_immatriculation ? ` · ${b.vehicle_immatriculation}` : ""}</span>
                </div>
                {b.notes && <p className="text-xs text-[#64748B] mt-1.5 italic">“{b.notes}”</p>}
              </div>
              {b.status === "confirmed" && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setStatus(b.id, "done")} data-testid={`done-${b.id}`} className="flex items-center gap-1 text-xs font-semibold border border-[#1A2642] rounded-md px-3 py-2 hover:border-[#00B4D8] transition"><Check className="w-3.5 h-3.5" /> Terminé</button>
                  <button onClick={() => setStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`} className="flex items-center gap-1 text-xs font-semibold text-[#EF476F] border border-[#EF476F]/30 rounded-md px-3 py-2 hover:bg-[#EF476F]/10 transition"><X className="w-3.5 h-3.5" /> Annuler</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const emptyService = { name: "", description: "", duration_minutes: 30, price: 40, active: true, order: 0 };

function ServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service obj or 'new'
  const [form, setForm] = useState(emptyService);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/services"); setServices(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...emptyService, order: services.length + 1 }); setEditing("new"); };
  const startEdit = (s) => { setForm({ ...s }); setEditing(s.id); };

  const save = async () => {
    const payload = { ...form, duration_minutes: Number(form.duration_minutes), price: Number(form.price), order: Number(form.order) };
    try {
      if (editing === "new") await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${editing}`, payload);
      toast.success("Prestation enregistrée"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette prestation ?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Loader2 className="w-7 h-7 animate-spin text-[#00B4D8]" />;

  return (
    <div data-testid="services-tab">
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-heading text-xl font-bold">Mes prestations</h2>
        <button onClick={startNew} data-testid="add-service-btn" className="flex items-center gap-1.5 bg-[#FFD60A] text-[#050810] font-bold text-sm rounded-full px-4 py-2 hover:bg-[#FFEA00] transition"><Plus className="w-4 h-4" /> Ajouter</button>
      </div>

      {editing && (
        <div className="bg-[#0C1322] border border-[#00B4D8]/40 rounded-lg p-5 mb-6" data-testid="service-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Nom *</label><input className={inputCls} data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea className={inputCls} rows={2} data-testid="service-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><label className={labelCls}>Durée (minutes) *</label><input type="number" min="5" step="5" className={inputCls} data-testid="service-duration" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
            <div><label className={labelCls}>Prix (€) *</label><input type="number" min="0" step="1" className={inputCls} data-testid="service-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div><label className={labelCls}>Ordre d'affichage</label><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-white cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-[#00B4D8]" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visible sur le site</label></div>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={save} data-testid="save-service-btn" className="flex items-center gap-1.5 bg-[#00B4D8] text-[#050810] font-bold text-sm rounded-full px-5 py-2.5 hover:bg-[#90E0EF] transition"><Save className="w-4 h-4" /> Enregistrer</button>
            <button onClick={() => setEditing(null)} className="border border-[#1A2642] rounded-full px-5 py-2.5 text-sm hover:border-white transition">Annuler</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {services.map((s) => (
          <div key={s.id} className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`service-row-${s.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold">{s.name}</span>
                {!s.active && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-[#475569] text-[#475569]">Masquée</span>}
              </div>
              <p className="text-sm text-[#94A3B8] mt-0.5">{s.duration_minutes} min · <span className="text-[#FFD60A] font-bold">{s.price} €</span></p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(s)} data-testid={`edit-service-${s.id}`} className="p-2 border border-[#1A2642] rounded-md hover:border-[#00B4D8] transition"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`} className="p-2 border border-[#EF476F]/30 text-[#EF476F] rounded-md hover:bg-[#EF476F]/10 transition"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newClosed, setNewClosed] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/settings"); setSettings(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateDay = (idx, field, value) => {
    setSettings((s) => ({ ...s, weekly_hours: { ...s.weekly_hours, [idx]: { ...s.weekly_hours[idx], [field]: value } } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        weekly_hours: settings.weekly_hours,
        slot_step_minutes: Number(settings.slot_step_minutes),
        closed_dates: settings.closed_dates || [],
        company_name: settings.company_name, phone: settings.phone, city: settings.city,
      };
      await api.put("/admin/settings", payload);
      toast.success("Horaires enregistrés"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading || !settings) return <Loader2 className="w-7 h-7 animate-spin text-[#00B4D8]" />;

  return (
    <div className="max-w-2xl" data-testid="hours-tab">
      <h2 className="font-heading text-xl font-bold mb-5">Horaires d'ouverture</h2>
      <div className="space-y-2">
        {WEEKDAYS_FULL.map((day, idx) => {
          const cfg = settings.weekly_hours[String(idx)] || { open: false, start: "09:00", end: "18:00" };
          return (
            <div key={day} className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-3 flex items-center gap-3" data-testid={`day-row-${idx}`}>
              <label className="flex items-center gap-2 w-32 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-[#00B4D8]" checked={cfg.open} data-testid={`day-open-${idx}`} onChange={(e) => updateDay(String(idx), "open", e.target.checked)} />
                <span className="font-semibold text-sm">{day}</span>
              </label>
              {cfg.open ? (
                <div className="flex items-center gap-2 text-sm">
                  <input type="time" className={`${inputCls} w-28`} value={cfg.start} onChange={(e) => updateDay(String(idx), "start", e.target.value)} />
                  <span className="text-[#94A3B8]">→</span>
                  <input type="time" className={`${inputCls} w-28`} value={cfg.end} onChange={(e) => updateDay(String(idx), "end", e.target.value)} />
                </div>
              ) : <span className="text-sm text-[#475569]">Fermé</span>}
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-[#0C1322] border border-[#1A2642] rounded-lg p-4">
        <label className={labelCls}>Intervalle entre créneaux (minutes)</label>
        <input type="number" min="5" step="5" className={`${inputCls} w-32`} data-testid="slot-step" value={settings.slot_step_minutes} onChange={(e) => setSettings({ ...settings, slot_step_minutes: e.target.value })} />
        <p className="text-xs text-[#64748B] mt-2">Détermine la granularité des créneaux proposés (ex : 15 min).</p>
      </div>

      <div className="mt-4 bg-[#0C1322] border border-[#1A2642] rounded-lg p-4">
        <label className={labelCls}>Jours de fermeture exceptionnelle</label>
        <div className="flex gap-2 mb-3">
          <input type="date" className={`${inputCls} w-44`} data-testid="closed-date-input" value={newClosed} onChange={(e) => setNewClosed(e.target.value)} />
          <button onClick={() => { if (newClosed && !settings.closed_dates.includes(newClosed)) { setSettings({ ...settings, closed_dates: [...settings.closed_dates, newClosed].sort() }); setNewClosed(""); } }}
            className="bg-[#00B4D8] text-[#050810] font-bold text-sm rounded-md px-4 hover:bg-[#90E0EF] transition">Ajouter</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(settings.closed_dates || []).map((d) => (
            <span key={d} className="flex items-center gap-1.5 bg-[#050810] border border-[#1A2642] rounded-md px-3 py-1.5 text-sm">
              {formatDateFR(d)}
              <button onClick={() => setSettings({ ...settings, closed_dates: settings.closed_dates.filter((x) => x !== d) })} className="text-[#EF476F]"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
          {(settings.closed_dates || []).length === 0 && <span className="text-sm text-[#475569]">Aucun</span>}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-hours-btn"
        className="mt-6 flex items-center gap-2 bg-[#FFD60A] text-[#050810] font-bold rounded-full px-6 py-3 hover:bg-[#FFEA00] disabled:opacity-50 transition">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer les horaires
      </button>
    </div>
  );
}


function EmailsTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connTesting, setConnTesting] = useState(false);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/email-config"); setCfg(r.data); setTestTo(r.data.notify_email || ""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        smtp_host: cfg.smtp_host,
        smtp_port: Number(cfg.smtp_port) || 465,
        smtp_user: cfg.smtp_user,
        smtp_ssl: cfg.smtp_ssl,
        sender_email: cfg.sender_email,
        sender_name: cfg.sender_name,
        notify_email: cfg.notify_email,
        emails_enabled: cfg.emails_enabled,
      };
      if (password.trim()) payload.smtp_password = password.trim();
      const r = await api.put("/admin/email-config", payload);
      setCfg(r.data); setPassword("");
      toast.success("Configuration SMTP enregistrée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setConnTesting(true);
    try {
      const r = await api.post("/admin/email-config/test-connection");
      if (r.data.status === "ok") toast.success(r.data.message || "Connexion SMTP réussie ✓");
      else toast.error(`Échec connexion : ${r.data.error || "erreur inconnue"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConnTesting(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/email-test", { to_email: testTo || undefined });
      if (r.data.status === "sent") toast.success(`Email de test envoyé à ${r.data.target}`);
      else if (r.data.status === "simulated") toast.message("Email simulé", { description: "Configurez le serveur SMTP et activez l'envoi puis réessayez." });
      else toast.error(`Échec : ${r.data.error || "erreur inconnue"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) return <Loader2 className="w-7 h-7 animate-spin text-[#00B4D8]" />;

  return (
    <div className="max-w-2xl" data-testid="emails-tab">
      <h2 className="font-heading text-xl font-bold mb-1">Configuration des emails (SMTP)</h2>
      <p className="text-sm text-[#94A3B8] mb-6">Envoi via votre serveur SMTP (SSL, port 465 recommandé). Tous les emails incluent le logo, vos coordonnées et un lien vers l'espace client.</p>

      <div className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-5 space-y-5">
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-[#1A2642]">
          <div className="flex items-center gap-3">
            <Power className={`w-5 h-5 ${cfg.emails_enabled ? "text-[#06D6A0]" : "text-[#475569]"}`} />
            <div>
              <p className="font-semibold text-white text-sm">Envoi des emails</p>
              <p className="text-xs text-[#94A3B8]">{cfg.emails_enabled ? "Activé" : "Désactivé (mode simulation)"}</p>
            </div>
          </div>
          <button onClick={() => setCfg({ ...cfg, emails_enabled: !cfg.emails_enabled })} data-testid="toggle-emails"
            className={`relative w-12 h-6 rounded-full transition ${cfg.emails_enabled ? "bg-[#06D6A0]" : "bg-[#1A2642]"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.emails_enabled ? "left-6" : "left-0.5"}`} />
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className={labelCls}>Serveur SMTP (hôte)</label>
            <input className={inputCls} data-testid="smtp-host" placeholder="smtp.votredomaine.fr" value={cfg.smtp_host} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Port</label>
            <input type="number" className={inputCls} data-testid="smtp-port" placeholder="465" value={cfg.smtp_port} onChange={(e) => setCfg({ ...cfg, smtp_port: e.target.value })} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
          <input type="checkbox" className="w-4 h-4 accent-[#00B4D8]" data-testid="smtp-ssl" checked={cfg.smtp_ssl} onChange={(e) => setCfg({ ...cfg, smtp_ssl: e.target.checked })} />
          Connexion sécurisée SSL/TLS (certificat) — recommandé sur le port 465
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Identifiant SMTP</label>
            <input className={inputCls} data-testid="smtp-user" placeholder="contact@votredomaine.fr" value={cfg.smtp_user} onChange={(e) => setCfg({ ...cfg, smtp_user: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Mot de passe SMTP</label>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[11px] font-bold px-2 py-1 rounded ${cfg.smtp_password_set ? "bg-[#06D6A0]/15 text-[#06D6A0]" : "bg-[#EF476F]/15 text-[#EF476F]"}`}>
                {cfg.smtp_password_set ? "Enregistré" : "Aucun"}
              </span>
            </div>
            <input type="password" className={inputCls} data-testid="smtp-password" placeholder={cfg.smtp_password_set ? "•••••••• (laisser vide pour conserver)" : "Mot de passe"} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nom expéditeur</label>
            <input className={inputCls} data-testid="sender-name" placeholder="ClimOmax13" value={cfg.sender_name} onChange={(e) => setCfg({ ...cfg, sender_name: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Email expéditeur</label>
            <input className={inputCls} data-testid="sender-email" placeholder="contact@votredomaine.fr" value={cfg.sender_email} onChange={(e) => setCfg({ ...cfg, sender_email: e.target.value })} />
            <p className="text-xs text-[#64748B] mt-1.5">Laissez vide pour utiliser l'identifiant SMTP.</p>
          </div>
        </div>

        <div>
          <label className={labelCls}>Email de notification (gérant)</label>
          <input className={inputCls} data-testid="notify-email" placeholder="vous@email.fr" value={cfg.notify_email} onChange={(e) => setCfg({ ...cfg, notify_email: e.target.value })} />
          <p className="text-xs text-[#64748B] mt-1.5">Reçoit les nouvelles réservations et annulations.</p>
        </div>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-[#1A2642]">
          <button onClick={save} disabled={saving} data-testid="save-email-config" className="flex items-center gap-2 bg-[#FFD60A] text-[#050810] font-bold rounded-full px-6 py-3 hover:bg-[#FFEA00] disabled:opacity-50 transition">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
          <button onClick={testConnection} disabled={connTesting} data-testid="test-connection" className="flex items-center gap-2 border border-[#1A2642] text-white font-semibold rounded-full px-6 py-3 hover:border-[#00B4D8] disabled:opacity-50 transition">
            {connTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4 text-[#06D6A0]" />} Tester la connexion
          </button>
        </div>

        <div className="pt-2">
          <label className={labelCls}>Envoyer un email de test à</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input className={inputCls} data-testid="test-to-email" placeholder="destinataire@email.fr" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <button onClick={sendTest} disabled={testing} data-testid="send-test-email" className="flex items-center justify-center gap-2 border border-[#1A2642] text-white font-semibold rounded-full px-6 py-3 hover:border-[#00B4D8] disabled:opacity-50 transition shrink-0">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-[#00B4D8]" />} Envoyer le test
            </button>
          </div>
        </div>
      </div>

      <EmailTemplatesEditor />
    </div>
  );
}

function EmailTemplatesEditor() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState({});
  const [type, setType] = useState("booking_confirmation");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const activeRef = useRef("body");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-templates");
      setData(r.data);
      setTemplates(JSON.parse(JSON.stringify(r.data.templates)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cur = templates[type] || { subject: "", body: "" };
  const setField = (field, value) => setTemplates((t) => ({ ...t, [type]: { ...t[type], [field]: value } }));

  const insertTag = (tag) => {
    const field = activeRef.current;
    const ref = field === "subject" ? subjectRef : bodyRef;
    const el = ref.current;
    const val = cur[field] || "";
    const start = el ? el.selectionStart : val.length;
    const end = el ? el.selectionEnd : val.length;
    const next = val.slice(0, start) + tag + val.slice(end);
    setField(field, next);
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + tag.length; } }, 0);
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/email-templates/preview", { template_type: type, subject: cur.subject, body: cur.body });
      setPreview(r.data.html);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/email-templates", { templates });
      setTemplates(JSON.parse(JSON.stringify(r.data.templates)));
      toast.success("Templates enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetOne = () => {
    if (!data) return;
    if (!window.confirm("Réinitialiser ce template au modèle d'origine ?")) return;
    setField("subject", data.defaults[type].subject);
    setField("body", data.defaults[type].body);
    toast.message("Template réinitialisé", { description: "Cliquez sur Enregistrer pour confirmer." });
  };

  if (loading || !data) return <div className="mt-8"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>;

  return (
    <div className="mt-8" data-testid="email-templates-editor">
      <h2 className="font-heading text-xl font-bold mb-1">Personnalisation des emails</h2>
      <p className="text-sm text-[#94A3B8] mb-5">Modifiez le sujet et le contenu de chaque email. Le logo (en-tête) et la signature (coordonnées + lien compte client) sont ajoutés automatiquement.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {data.types.map((t) => (
          <button key={t} onClick={() => { setType(t); setPreview(""); }} data-testid={`tpl-type-${t}`}
            className={`text-xs font-semibold px-3 py-2 rounded-full border transition ${type === t ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "border-[#1A2642] text-[#94A3B8] hover:border-[#00B4D8]"}`}>
            {data.labels[t]}
          </button>
        ))}
      </div>

      <div className="bg-[#0C1322] border border-[#1A2642] rounded-lg p-5 space-y-4">
        <div>
          <label className={labelCls}>Sujet de l'email</label>
          <input ref={subjectRef} onFocus={() => (activeRef.current = "subject")} className={inputCls} data-testid="tpl-subject" value={cur.subject} onChange={(e) => setField("subject", e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Contenu du message</label>
          <textarea ref={bodyRef} onFocus={() => (activeRef.current = "body")} rows={10} className={`${inputCls} font-mono text-xs leading-relaxed`} data-testid="tpl-body" value={cur.body} onChange={(e) => setField("body", e.target.value)} />
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] mb-2">Balises disponibles (cliquez pour insérer)</p>
          <div className="flex flex-wrap gap-1.5">
            {data.tags.map((t) => (
              <button key={t.tag} onClick={() => insertTag(t.tag)} title={t.desc} data-testid={`tpl-tag-${t.tag.replace(/[{}]/g, "")}`}
                className="text-[11px] font-mono bg-[#050810] border border-[#1A2642] rounded-md px-2 py-1 text-[#00B4D8] hover:border-[#00B4D8] transition">
                {t.tag}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-[#1A2642]">
          <button onClick={save} disabled={saving} data-testid="save-templates" className="flex items-center gap-2 bg-[#FFD60A] text-[#050810] font-bold rounded-full px-6 py-3 hover:bg-[#FFEA00] disabled:opacity-50 transition">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
          <button onClick={doPreview} disabled={previewing} data-testid="preview-template" className="flex items-center gap-2 border border-[#1A2642] text-white font-semibold rounded-full px-6 py-3 hover:border-[#00B4D8] disabled:opacity-50 transition">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 text-[#00B4D8]" />} Aperçu
          </button>
          <button onClick={resetOne} data-testid="reset-template" className="flex items-center gap-2 border border-[#EF476F]/30 text-[#EF476F] font-semibold rounded-full px-6 py-3 hover:bg-[#EF476F]/10 transition">
            <RotateCcw className="w-4 h-4" /> Réinitialiser
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-5" data-testid="template-preview">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] mb-2">Aperçu de l'email</p>
          <iframe title="preview" srcDoc={preview} className="w-full h-[600px] rounded-lg border border-[#1A2642] bg-white" />
        </div>
      )}
    </div>
  );
}
